import { Controller, useForm } from 'react-hook-form'

import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { BusinessModelCard } from '@/shared/components/BusinessModelCard/BusinessModelCard'
import { SelectDropdown } from '@/shared/components/SelectDropdown/SelectDropdown'
import { pxToRem } from '@/shared/styles/themes'
import { Box, Grid2 as Grid, styled, Typography } from '@mui/material'

import { GeneralPreferencesFormValues } from '../models/OnboardingFormFields'

export type GeneralPreferencesFormProps = {
  initialValues: GeneralPreferencesFormValues
  onSubmit: (data: GeneralPreferencesFormValues) => void
}

export const GeneralPreferencesForm: React.FC<GeneralPreferencesFormProps> = ({
  initialValues,
  onSubmit,
}) => {
  const { control, handleSubmit } = useForm<GeneralPreferencesFormValues>({
    defaultValues: initialValues,
  })


  // Units of measurement options
  const distanceUnitOptions: DropdownOption[] = [
    { label: 'Kilometers (km)', value: 'km' },
    { label: 'Miles (mi)', value: 'mi' },
  ]

  const weightUnitOptions: DropdownOption[] = [
    { label: 'Kilograms (kg)', value: 'kg' },
    { label: 'Pounds (lb)', value: 'lb' },
  ]

  const volumeUnitOptions: DropdownOption[] = [
    { label: 'Cubic Meters (m³)', value: 'cbm' },
    { label: 'Cubic Feet (ft³)', value: 'cbf' },
    { label: 'Liters (L)', value: 'l' },
  ]

  const onFormSubmit = (data: GeneralPreferencesFormValues) => {
    onSubmit(data)
  }

  return (
    <StyledForm id="onboarding-step-0" onSubmit={handleSubmit(onFormSubmit)}>
      <Typography variant="h2" sx={{ fontSize: pxToRem(24), fontWeight: 500, mb: 2 }}>
        General Preferences
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Set up your default preferences to customize your FastMile experience. These settings can be changed later.
      </Typography>

      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" gutterBottom marginTop={1} marginBottom={2}>
            Business Model
          </Typography>

          <Controller
            name="businessModel"
            control={control}
            render={({ field }) => (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <BusinessModelCard
                  title="Depot"
                  description="All deliveries and pickups flow through a central hub. Drivers begin and end their routes at a depot."
                  imageSrc="/svgs/depot.svg"
                  isSelected={field.value === BusinessModelEnum.Depot}
                  onClick={() => field.onChange(BusinessModelEnum.Depot)}
                />
                <BusinessModelCard
                  disabled
                  title="Point to Point (Comming Soon)"
                  description="Direct pickup and delivery between locations. No central hub required - drivers move directly between pickup and dropoff points."
                  imageSrc="/svgs/p2p.svg"
                  isSelected={field.value === BusinessModelEnum.Courier}
                  onClick={() => field.onChange(BusinessModelEnum.Courier)}
                />
              </Box>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" gutterBottom marginTop={4}>
            Units of Measurement
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="distanceUnit"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                {...field}
                freeSolo={false}
                disableClearable={true}
                label="Distance Unit"
                placeholder="Select distance unit"
                options={distanceUnitOptions}
                value={distanceUnitOptions.find(option => option.value === field.value) || null}
                onChange={(_, option) => {
                  if (option) {
                    field.onChange((option as DropdownOption)?.value)
                  }

                  field.onChange(option?.value)
                }}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="weightUnit"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                {...field}
                freeSolo={false}
                disableClearable={true}
                label="Weight Unit"
                placeholder="Select weight unit"
                options={weightUnitOptions}
                value={weightUnitOptions.find(option => option.value === field.value) || null}
                onChange={(_, option) => {
                  if (option) {
                    field.onChange((option as DropdownOption)?.value)
                  }

                  field.onChange(option?.value)
                }} fullWidth
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="volumeUnit"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                {...field}
                freeSolo={false}
                disableClearable={true}
                label="Volume Unit"
                placeholder="Select volume unit"
                options={volumeUnitOptions}
                value={volumeUnitOptions.find(option => option.value === field.value) || null}
                onChange={(_, option) => {
                  if (option) {
                    field.onChange((option as DropdownOption)?.value)
                  }

                  field.onChange(option?.value)
                }}
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>
    </StyledForm>
  )
}

const StyledForm = styled('form')`
  width: 100%;
`